/**
 * AI Labs Page
 * Integrates all four advanced AI capabilities:
 * - Excel Copilot
 * - Workbook Analyzer
 * - Scenario Simulation
 * - Natural Language Query Engine
 */

import React, { useState, useEffect, useCallback } from 'react';
import { CopilotPanel } from '../components/features/CopilotPanel';
import { WorkbookAnalyzerPanel } from '../components/features/WorkbookAnalyzerPanel';
import { ScenarioResultsPanel } from '../components/features/ScenarioResultsPanel';
import { NLQueryPanel } from '../components/features/NLQueryPanel';
import { DependencyGraphPanel } from '../components/features/DependencyGraphPanel';
import { WorkbookRiskPanel } from '../components/features/WorkbookRiskPanel';
import { WorkbookPerformancePanel } from '../components/features/WorkbookPerformancePanel';
import { Header } from '../components/common/Header';
import type {
  CopilotExecutionContext,
  WorksheetMetadata,
  WorkbookTableContext,
} from '@excel-ai-studio/shared';

type TabType = 'copilot' | 'analyzer' | 'scenario' | 'query' | 'dependencies' | 'risks' | 'performance';

interface AnalysisHistoryEntry {
  timestamp: string;
  type: 'risk' | 'performance';
  score: number;
  label: string;
}

const HISTORY_KEY = 'ailabs_analysis_history';
const MAX_HISTORY = 20;

function loadHistory(): AnalysisHistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as AnalysisHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function saveHistoryEntry(entry: AnalysisHistoryEntry) {
  const history = loadHistory();
  history.unshift(entry);
  if (history.length > MAX_HISTORY) history.length = MAX_HISTORY;
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
}

/**
 * AI Labs Page Component
 */
export const AILabsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('copilot');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<{
    address: string;
    values: unknown[][];
    headers?: string[];
  } | undefined>(undefined);
  const [sheetName, setSheetName] = useState('');
  const [workbookName, setWorkbookName] = useState('');
  const [analysisHistory, setAnalysisHistory] = useState<AnalysisHistoryEntry[]>(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [worksheetMetadata, setWorksheetMetadata] = useState<WorksheetMetadata | undefined>();
  const [tableSchemas, setTableSchemas] = useState<WorkbookTableContext[]>([]);

  // Initialize context on mount (non-blocking: page renders immediately)
  useEffect(() => {
    initializeContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadExcelData = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      await Excel.run(async (context) => {
        const workbook = context.workbook;
        workbook.load('name');

        const sheet = workbook.worksheets.getActiveWorksheet();
        sheet.load('name');

        const usedRange = sheet.getUsedRange();
        usedRange.load(['address', 'values', 'rowCount', 'columnCount', 'formulas']);
        const tables = workbook.tables;
        tables.load('items/name');

        await context.sync();

        const values = usedRange.values as unknown[][];
        const formulas = usedRange.formulas as unknown[][];
        const discoveredTables: WorkbookTableContext[] = [];

        for (const table of tables.items) {
          const tableSheet = table.worksheet;
          const tableRange = table.getRange();
          const headerRange = table.getHeaderRowRange();
          tableSheet.load('name');
          tableRange.load(['address', 'values']);
          headerRange.load('values');
          await context.sync();

          const headerValues = (headerRange.values?.[0] as unknown[] | undefined) ?? [];
          const headers = headerValues.map((cell) => String(cell ?? '').trim());
          const tableValues = (tableRange.values as unknown[][]) ?? [];
          const bodyValues = tableValues.length > 1 ? tableValues.slice(1) : [];

          if (headers.length === 0) continue;
          discoveredTables.push({
            tableId: table.name,
            sheetName: tableSheet.name,
            rangeAddress: tableRange.address,
            headers,
            values: bodyValues,
          });
        }

        setWorkbookName(workbook.name);
        setSheetName(sheet.name);
        setTableSchemas(discoveredTables);
        setSelectedRange({
          address: usedRange.address,
          values,
          headers: values.length > 0 ? (values[0] as string[]) : [],
        });
        setWorksheetMetadata({
          rowCount: usedRange.rowCount,
          columnCount: usedRange.columnCount,
          usedRange: usedRange.address,
          hasFormulas: formulas.some((row) =>
            row.some((cell) => typeof cell === 'string' && cell.startsWith('='))
          ),
          hasCharts: false,
          hasTables: discoveredTables.length > 0,
        });
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setLoadError(`Impossible de lire les données Excel : ${msg}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Initialize Excel context - reads real data from the active worksheet
   */
  const initializeContext = async () => {
    await loadExcelData();
  };

  /**
   * Refresh context from current worksheet
   */
  const refreshContext = async () => {
    await loadExcelData();
  };

  /**
   * Get copilot context
   */
  const getCopilotContext = (): CopilotExecutionContext => ({
    selectedRange,
    sheetName,
    workbookName,
    worksheetMetadata,
    tableSchemas,
  });

  const recordAnalysis = useCallback((type: 'risk' | 'performance', score: number, label: string) => {
    const entry: AnalysisHistoryEntry = {
      timestamp: new Date().toISOString(),
      type,
      score,
      label,
    };
    saveHistoryEntry(entry);
    setAnalysisHistory(loadHistory());
  }, []);

  if (loadError && !selectedRange) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#c00' }}>
        <p>{loadError}</p>
        <button
          onClick={initializeContext}
          style={{ marginTop: '12px', padding: '8px 16px', cursor: 'pointer' }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header />

      {/* Tab Navigation */}
      <div
        style={{
          display: 'flex',
          borderBottom: '2px solid #ddd',
          backgroundColor: '#f5f5f5',
          padding: '0 16px',
          overflowX: 'auto',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', overflowX: 'auto' }}>
          {[
            { id: 'copilot', label: '🤖 Copilot' },
            { id: 'analyzer', label: '🔍 Analyzer' },
            { id: 'scenario', label: '📈 Scenario' },
            { id: 'query', label: '🔎 Query' },
            { id: 'dependencies', label: '🔗 Dependencies' },
            { id: 'risks', label: '⚠️ Risks' },
            { id: 'performance', label: '⚡ Performance' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{
                padding: '12px 16px',
                backgroundColor: activeTab === tab.id ? 'white' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #2196F3' : 'none',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                color: activeTab === tab.id ? '#2196F3' : '#666',
                fontSize: '13px',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={refreshContext}
          style={{
            padding: '8px 12px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            marginRight: '8px',
            whiteSpace: 'nowrap',
          }}
        >
          🔄 Rafraîchir
        </button>
      </div>

      {/* Tab Content */}
      {/* Tab Content — all panels stay mounted to preserve state (caching) */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {isLoading && (
          <div style={{ padding: '8px 12px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#1565c0' }}>
            Chargement des données Excel...
          </div>
        )}

        <div style={{ display: activeTab === 'copilot' ? 'block' : 'none' }}>
          <CopilotPanel
            context={getCopilotContext()}
            onCommandExecuted={(result) => {
              console.log('Copilot command executed:', result);
            }}
          />
        </div>

        <div style={{ display: activeTab === 'analyzer' ? 'block' : 'none' }}>
          <WorkbookAnalyzerPanel
            onAnalysisComplete={(report) => {
              console.log('Workbook analysis complete:', report);
            }}
          />
        </div>

        <div style={{ display: activeTab === 'scenario' ? 'block' : 'none' }}>
          <ScenarioResultsPanel
            baselineData={selectedRange?.values}
            headers={selectedRange?.headers}
            onScenarioCreated={(result) => {
              console.log('Scenario created:', result);
            }}
          />
        </div>

        <div style={{ display: activeTab === 'query' ? 'block' : 'none' }}>
          <NLQueryPanel
            data={selectedRange?.values}
            headers={selectedRange?.headers}
            onQueryExecuted={(result) => {
              console.log('Query executed:', result);
            }}
          />
        </div>

        <div style={{ display: activeTab === 'dependencies' ? 'block' : 'none' }}>
          <DependencyGraphPanel
            onAnalysisComplete={(graph) => {
              console.log('Dependency analysis complete:', graph);
            }}
          />
        </div>

        <div style={{ display: activeTab === 'risks' ? 'block' : 'none' }}>
          <WorkbookRiskPanel
            onAnalysisComplete={(report) => {
              recordAnalysis('risk', report.riskScore, `Risque: ${report.riskScore}/100 — ${report.criticalCount} critique(s)`);
            }}
          />
        </div>

        <div style={{ display: activeTab === 'performance' ? 'block' : 'none' }}>
          <WorkbookPerformancePanel
            onAnalysisComplete={(report) => {
              recordAnalysis('performance', report.score, `Perf: ${report.score}/100 (${report.overallRating})`);
            }}
          />
        </div>
      </div>

      {/* Footer Info */}
      <div
        style={{
          padding: '12px 16px',
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #ddd',
          fontSize: '12px',
          color: '#666',
        }}
      >
        {loadError && <p style={{ color: '#c00', margin: '0 0 4px' }}>{loadError}</p>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: '0' }}>
            Classeur: <strong>{workbookName || '—'}</strong> |
            Feuille: <strong>{sheetName || '—'}</strong> |
            Plage: <strong>{selectedRange?.address || '—'}</strong> |
            Lignes: <strong>{selectedRange?.values.length ?? '—'}</strong>
          </p>
          {analysisHistory.length > 0 && (
            <button
              onClick={() => setShowHistory((v) => !v)}
              style={{ padding: '2px 8px', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', backgroundColor: '#fff' }}
            >
              Historique ({analysisHistory.length})
            </button>
          )}
        </div>
        {showHistory && analysisHistory.length > 0 && (
          <div style={{ marginTop: '8px', maxHeight: '120px', overflowY: 'auto', borderTop: '1px solid #ddd', paddingTop: '6px' }}>
            {analysisHistory.slice(0, 10).map((entry, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '11px', color: entry.type === 'risk' ? '#d32f2f' : '#1565c0' }}>
                <span>{entry.label}</span>
                <span style={{ color: '#999' }}>{new Date(entry.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
